/**
 * Linear obstacles (03 §5.1 step 3): one per band boundary - a trench line, a wall, wire or
 * a hedge - spanning the width with at least two crossings, plus the north-south ravines
 * that thread the quiet crossings and give the player his stealth highway, and the few
 * hazards of the drop band (step 8).
 */

import type { Pcg32 } from '../sim/rng';
import type { PropDef } from '../sim/types';
import { WALLS } from '../sim/tuningWalls';
import type { FitOptions, MapBuilder } from './builder';
import { FLAG } from './grid';
import { LINES, RAVINE } from './tuning';
import type { Gap, GenCtx, LinePlan } from './types';

type RunKind = 'wall' | 'wire' | 'hedge' | 'pipeline' | 'sandbags';

/** The stretches of [from, to] left once the gaps are cut out. */
export function intervals(from: number, to: number, gaps: readonly Gap[]): Array<[number, number]> {
  let out: Array<[number, number]> = [[from, to]];
  for (const g of gaps) {
    const a = g.x - g.width / 2;
    const b = g.x + g.width / 2;
    const next: Array<[number, number]> = [];
    for (const [x0, x1] of out) {
      if (b <= x0 || a >= x1) next.push([x0, x1]);
      else {
        if (a > x0) next.push([x0, a]);
        if (b < x1) next.push([b, x1]);
      }
    }
    out = next;
  }
  return out.filter(([x0, x1]) => x1 - x0 >= 0.8);
}

/**
 * The longest piece a run of this kind is laid in. A wall is laid short (ROUND7): a breach takes whole pieces,
 * so the piece decides the gap - a 12 m piece let one LAW open 8.8 m of the north face and a bomb 17.6 m.
 */
export const pieceMaxOf = (kind: RunKind): number => (kind === 'wall' ? WALLS.pieceMax : LINES.pieceMax);

/** An east-west run of wall, wire, hedge or pipe in pieces, leaving the gaps open. Returns the pieces. */
export function buildRun(
  map: MapBuilder, kind: RunKind, z: number, from: number, to: number, gaps: readonly Gap[],
): PropDef[] {
  const pieces: PropDef[] = [];
  for (const [x0, x1] of intervals(from, to, gaps)) {
    const count = Math.ceil((x1 - x0) / pieceMaxOf(kind));
    const length = (x1 - x0) / count;
    for (let i = 0; i < count; i++) pieces.push(map.add(kind, x0 + length * (i + 0.5), z, { length }));
  }
  return pieces;
}

/**
 * A free-standing east-west run centred on (x, z) - the approach bands' cover runs (scatter.ts) - if its whole
 * footprint fits; laid in pieces like any other run, so a wall there breaches like the compound's. Null if not.
 */
export function placeRun(
  map: MapBuilder, kind: 'hedge' | 'wall' | 'sandbags', x: number, z: number, o: FitOptions & { length: number },
): PropDef[] | null {
  if (!map.fits(kind, x, z, o)) return null;
  return buildRun(map, kind, z, x - o.length / 2, x + o.length / 2, []);
}

/** Keep every crossing clear of scatter, ponds and mines. */
export function reserveGaps(map: MapBuilder, z: number, gaps: readonly Gap[]): void {
  for (const g of gaps) map.mask.circle(g.x, z, g.width / 2 + 0.5, FLAG.keep);
}

function buildTrenchLine(ctx: GenCtx, line: LinePlan): void {
  const rng = ctx.rng.fork(`trench-line-${line.z}`);
  const [w0, w1] = ctx.kit.depression.width;
  const [d0, d1] = ctx.kit.depression.depth;
  for (const [x0, x1] of intervals(0, ctx.sizeX, line.gaps)) {
    const steps = Math.max(1, Math.round((x1 - x0) / 13));
    const points: Array<[number, number]> = [];
    for (let i = 0; i <= steps; i++) {
      const end = i === 0 || i === steps;
      points.push([x0 + ((x1 - x0) * i) / steps, line.z + (end ? 0 : rng.range(-1.3, 1.3))]);
    }
    ctx.map.trench(points, rng.range(w0, w1), rng.range(d0, d1));
  }
}

export function buildLines(ctx: GenCtx): void {
  for (const line of ctx.plan.lines) {
    reserveGaps(ctx.map, line.z, line.gaps);
    if (line.kind === 'trench') buildTrenchLine(ctx, line);
    else buildRun(ctx.map, line.kind, line.z, 0, ctx.sizeX, line.gaps);
  }
}

/**
 * The belt is a killing field (03 §5.1 step 5), so the last ravine does not stop under its guns: it pokes
 * through the wire and carries on to the belt's far side - or, where a water barrier spans the belt, to the
 * south bank, and the crossing itself is made in the open.
 */
function throughBelt(ctx: GenCtx, rng: Pcg32, wire: LinePlan): Array<[number, number]> {
  const belt = ctx.bands[ctx.bands.length - 2];
  if (!belt || belt.kind !== 'belt') return [];
  const zEnd = ctx.spec.river ? (belt.z0 + belt.z1) / 2 + RAVINE.riverStandOff : belt.z0 + RAVINE.beltStandOff;
  const zFrom = wire.z - 3;
  if (zFrom - zEnd < 4) return [];
  const x = (spread: number): number => Math.max(5, Math.min(ctx.sizeX - 5, wire.quietX + rng.range(-spread, spread)));
  // A barrier is only crossed at its quiet crossing: end square on to it.
  return [[x(4), zFrom - (zFrom - zEnd) * 0.5], [ctx.spec.river ? wire.quietX : x(3), zEnd]];
}

/**
 * One north-south ravine per approach band, from the quiet crossing of the line south of
 * it to the quiet crossing of the line north of it. Each starts where the last one ended,
 * so the chain is one continuous crawl from the drop band to the belt.
 */
export function buildRavines(ctx: GenCtx): void {
  const rng = ctx.rng.fork('ravines');
  const { lines } = ctx.plan;
  const [w0, w1] = ctx.kit.depression.width;
  const [d0, d1] = ctx.kit.depression.depth;
  const width = Math.min(rng.range(w0, w1), LINES.quietGap - 2);
  const depth = rng.range(d0, d1);
  for (let i = 1; i < lines.length; i++) {
    const south = lines[i - 1];
    const north = lines[i];
    if (!south || !north) continue;
    const last = i === lines.length - 1;
    const zStart = south.z + 2.5;
    const zEnd = last ? north.z - 3 : north.z + 2.5;
    const bends = 3;
    const points: Array<[number, number]> = [[south.quietX, zStart]];
    // Hold the line straight through the crossing, then wander.
    points.push([south.quietX, south.z - 3]);
    for (let b = 1; b < bends; b++) {
      const t = b / bends;
      const x = south.quietX + (north.quietX - south.quietX) * t + rng.range(-5, 5);
      points.push([Math.max(5, Math.min(ctx.sizeX - 5, x)), south.z - 3 + (north.z + 6 - (south.z - 3)) * t]);
    }
    points.push([north.quietX, north.z + 6]);
    points.push([north.quietX, zEnd]);
    if (last) points.push(...throughBelt(ctx, rng, north));
    ctx.map.trench(points, width, depth);
    const end = points[points.length - 1];
    if (last && end) ctx.quiet.end = { x: end[0], z: end[1] };
  }
}

/** A couple of short trenches and a wire run, so that where you land matters (03 §5.1 step 8). */
export function buildDropHazards(ctx: GenCtx): void {
  const rng = ctx.rng.fork('drop-hazards');
  const drop = ctx.bands[0];
  if (!drop) return;
  const [d0, d1] = ctx.kit.depression.depth;
  const avoid = FLAG.keep | FLAG.road | FLAG.trench | FLAG.solid;
  let trenches = 1 + rng.int(2);
  for (let tries = 0; tries < 14 && trenches > 0; tries++) {
    const length = rng.range(12, 22);
    const x = rng.range(8, ctx.sizeX - 8 - length);
    const z = rng.range(drop.z0 + 10, drop.z1 - 8);
    const slope = rng.range(-5, 5);
    const a: [number, number] = [x, z];
    const m: [number, number] = [x + length / 2, z + slope / 2 + rng.range(-1, 1)];
    const b: [number, number] = [x + length, z + slope];
    const points = [a, m, b];
    const clear = (p: [number, number], q: [number, number]): boolean =>
      ctx.map.mask.clearLine(p[0], p[1], q[0], q[1], avoid);
    if (!clear(a, m) || !clear(m, b)) continue;
    ctx.map.trench(points, 2.2, rng.range(d0, d1));
    trenches--;
  }
  for (let tries = 0; tries < 10; tries++) {
    const length = Math.round(rng.range(8, 12));
    const x = rng.range(8 + length / 2, ctx.sizeX - 8 - length / 2);
    const z = rng.range(drop.z0 + 8, drop.z1 - 10);
    if (ctx.map.place('wire', x, z, { length, margin: 2 })) break;
  }
}
